import type {
  ListaItens,
  SeletorItem,
} from '@Lar21/imaginii-web-components/vue'

import type {
  FiltrosCatalogoRegraDeCalculo,
  OpcaoRegraDeCalculo,
  SelecaoCatalogoRegraDeCalculo,
} from '../catalogo-regra-de-calculo.tipos'

export const CHAVE_I18N_CATALOGO_REGRA_DE_CALCULO =
  'srcNext.folhaDePonto.catalogoRegraDeCalculo'

const CHAVE_OPCAO_NULA = '__pontotel_regra_de_calculo_nula__'

type Traduzir = (chave: string) => string

export const criarItemSeletorRegraDeCalculo = (
  opcao: OpcaoRegraDeCalculo,
): SeletorItem => ({
  chave: opcao.id,
  valor: opcao.name,
  ...(typeof opcao.code === 'string' && opcao.code.trim() ? { legenda: opcao.code } : {}),
})

const normalizarOpcoesSelecionadas = (
  valor: SelecaoCatalogoRegraDeCalculo,
  selecaoMultipla: boolean,
): readonly OpcaoRegraDeCalculo[] => {
  if (valor === null) return []
  if (!Array.isArray(valor)) return [valor as OpcaoRegraDeCalculo]
  if (selecaoMultipla) return valor as readonly OpcaoRegraDeCalculo[]
  return valor[0] ? [valor[0] as OpcaoRegraDeCalculo] : []
}

export const filtrarOpcoesRegraDeCalculo = (
  opcoes: readonly OpcaoRegraDeCalculo[],
  filtros?: FiltrosCatalogoRegraDeCalculo,
): readonly OpcaoRegraDeCalculo[] => {
  if (!filtros || Object.keys(filtros).length === 0) return opcoes

  const criterios = Object.entries(filtros)
  return opcoes.filter((opcao) => criterios.every(([campo, valor]) => (
    valor === undefined || opcao[campo as keyof OpcaoRegraDeCalculo] === valor
  )))
}

export const criarEstadoSeletorCatalogoRegraDeCalculo = (
  opcoes: readonly OpcaoRegraDeCalculo[],
  valor: SelecaoCatalogoRegraDeCalculo,
  selecaoMultipla: boolean,
  opcaoNula?: string,
): ListaItens => {
  const carregados = opcoes.filter(opcao => typeof opcao.id === 'string' && opcao.id.trim()
    && typeof opcao.name === 'string' && opcao.name.trim()).map(criarItemSeletorRegraDeCalculo)
  const itensPorId = new Map(
    carregados.map((item) => [String(item.chave), item]),
  )
  const selecionados = normalizarOpcoesSelecionadas(valor, selecaoMultipla)
    .flatMap((opcao) => {
      const item = itensPorId.get(opcao.id)
      return item ? [item] : []
    })

  if (opcaoNula !== undefined && !selecaoMultipla) {
    const itemNulo = { chave: CHAVE_OPCAO_NULA, valor: opcaoNula }
    carregados.unshift(itemNulo)
    if (valor === null) selecionados.push(itemNulo)
  }

  return { carregados, selecionados }
}

export const extrairValorDoSeletorCatalogoRegraDeCalculo = (
  estado: ListaItens,
  opcoes: readonly OpcaoRegraDeCalculo[],
  selecaoMultipla: boolean,
): SelecaoCatalogoRegraDeCalculo => {
  const opcoesPorId = new Map(opcoes.map((opcao) => [opcao.id, opcao]))
  const idsSelecionados = [...new Set(
    estado.selecionados.map(({ chave }) => String(chave)),
  )]
  const opcoesSelecionadas = idsSelecionados.flatMap((id) => {
    const opcao = opcoesPorId.get(id)
    return opcao ? [opcao] : []
  })
  return selecaoMultipla
    ? opcoesSelecionadas
    : (opcoesSelecionadas[0] ?? null)
}

export const criarPropriedadesPontotelSeletorRegraDeCalculo = ({
  traduzir,
  label,
  placeholder,
  erro,
}: Readonly<{
  traduzir: Traduzir
  label?: string
  placeholder?: string
  erro: string | null
}>) => ({
  tamanho: 'M' as const,
  largura: '100%',
  tamanhoPagina: 20,
  pesquisavel: true,
  recarregarTodos: true,
  label: label ?? traduzir(`${CHAVE_I18N_CATALOGO_REGRA_DE_CALCULO}.label`),
  placeholder: placeholder
    ?? traduzir(`${CHAVE_I18N_CATALOGO_REGRA_DE_CALCULO}.placeholder`),
  erro,
  traducaoNenhumResultado: traduzir(
    `${CHAVE_I18N_CATALOGO_REGRA_DE_CALCULO}.nenhumResultado`,
  ),
  traducaoSelecionado: traduzir(
    `${CHAVE_I18N_CATALOGO_REGRA_DE_CALCULO}.selecionado`,
  ),
  traducaoSelecionadosPlural: traduzir(
    `${CHAVE_I18N_CATALOGO_REGRA_DE_CALCULO}.selecionados`,
  ),
  traducaoSelecionarTodos: traduzir(
    `${CHAVE_I18N_CATALOGO_REGRA_DE_CALCULO}.selecionarTodos`,
  ),
  traducaoItens: traduzir(`${CHAVE_I18N_CATALOGO_REGRA_DE_CALCULO}.itens`),
  traducaoRecarregarTodos: traduzir(
    `${CHAVE_I18N_CATALOGO_REGRA_DE_CALCULO}.recarregar`,
  ),
  traducaoRecarregado: traduzir(
    `${CHAVE_I18N_CATALOGO_REGRA_DE_CALCULO}.recarregado`,
  ),
})
